import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, Save } from "lucide-react";
import { toast } from "sonner";

import { Layout } from "@/components/Layout";
import { HoardexaLoader } from "@/components/HoardexaLoader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { useHoardingTypes } from "@/lib/hoardingTypes";
import { useAuth } from "@/lib/auth";
import { hasAnyPermission } from "@/lib/permissions";
import { RegisterSearch } from "@/components/RegisterSearch";
import { PaginationFooter } from "@/components/PaginationFooter";
import {
  EMPTY,
  asArray,
  chargeFor,
  campaignChargeTotal,
  campaignContractTotal,
  defaultChargeForHoarding,
  latestBusinessMargins,
  marginProfit,
  updateCharge,
  validateCampaignMargins,
} from "./utils";

function Field({ label, required, children, note }) {
  return (
    <label className="space-y-1">
      <span className="text-xs font-bold text-slate-600">
        {label} {required && <span className="text-blue-500">*</span>}
      </span>
      {children}
      {note && <p className="text-[11px] font-semibold text-slate-500">{note}</p>}
    </label>
  );
}

function Section({ title, children, right }) {
  return (
    <section className="rounded-lg border border-slate-300 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <h2 className="text-sm font-black text-slate-950">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function partnerListingToCampaignHoarding(listing) {
  const printingByOwner = listing.printing_responsibility === "owner";
  const mountingByOwner = listing.mounting_responsibility === "owner";

  return {
    ...listing,
    id: `partner:${listing.id}`,
    source_listing_id: listing.id,
    inventory_source: "partner",
    hoarding_id: listing.hoarding_code || listing.hoarding_id,
    monthly_price: Number(listing.min_display_charge || 0),
    default_printing_charge: printingByOwner ? Number(listing.min_printing_charge || 0) : 0,
    default_installation_charge: mountingByOwner ? Number(listing.min_mounting_charge || 0) : 0,
    default_display_margin_type: "percent",
    default_display_margin_value: Number(listing.effective_commission_pct ?? listing.commission_pct ?? 0),
    owner_name: listing.owner_name || "Partner owner",
  };
}

function withPartnerDisplayMarginDefaults(charge, hoarding) {
  if (hoarding?.inventory_source !== "partner") return charge;
  return {
    ...charge,
    display_margin_type: charge.display_margin_type || hoarding.default_display_margin_type || "percent",
    display_margin_value: Number(charge.display_margin_value || hoarding.default_display_margin_value || 0),
  };
}

export function CreateCampaign() {
  const location = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState(EMPTY);
  const [hoardings, setHoardings] = useState([]);
  const [partnerHoardings, setPartnerHoardings] = useState([]);
  const [showPartnerInventory, setShowPartnerInventory] = useState(false);
  const [inventoryTab, setInventoryTab] = useState("primary");
  const [clients, setClients] = useState([]);
  const [businessMargins, setBusinessMargins] = useState([]);
  const [accountingSettings, setAccountingSettings] = useState(null);
  const [hoardingSearch, setHoardingSearch] = useState("");
  const [committedHoardingSearch, setCommittedHoardingSearch] = useState("");
  const [hoardingFilters, setHoardingFilters] = useState({ city: "", type: "" });
  const [hoardingPage, setHoardingPage] = useState(1);
  const [hoardingPageSize, setHoardingPageSize] = useState(10);
  const [initialSelectedIds, setInitialSelectedIds] = useState([]);
  const [expandedChargeRows, setExpandedChargeRows] = useState(() => new Set());
  const { names: hoardingTypes } = useHoardingTypes();

  const canAssignHoardings = hasAnyPermission(user, ["assign_hoardings_to_campaign", "create_campaigns"]);
  const canChangeDates = hasAnyPermission(user, ["change_campaign_dates", "create_campaigns"]);
  const canEditAmounts = hasAnyPermission(user, ["edit_campaign_amounts", "change_campaign_amounts", "create_campaigns"]);
  const canViewRevenue = hasAnyPermission(user, ["view_campaign_amounts", "view_basic_revenue", "view_profit_and_loss", "view_financial_reports"]);

  const today = new Date().toISOString().slice(0, 10);

  const isPartnerHoardingId = (id) => String(id || "").startsWith("partner:");
  const allAssignableHoardings = useMemo(() => [...hoardings, ...partnerHoardings], [hoardings, partnerHoardings]);
  const selectedPrimaryCount = form.hoarding_ids.filter((id) => !isPartnerHoardingId(id)).length;
  const selectedPartnerCount = form.hoarding_ids.filter(isPartnerHoardingId).length;

  const contractTotal = campaignContractTotal(form, allAssignableHoardings);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/campaigns/init", { skipGlobalLoader: true }),
      api.get("/accounts/settings", { skipGlobalLoader: true }).catch(() => ({ data: null })),
      api.get("/partner-network/settings", { skipGlobalLoader: true }).catch(() => ({ data: null })),
      api.get("/partner-network/listings?side=host", { skipGlobalLoader: true }).catch(() => ({ data: [] })),
    ])
      .then(([initRes, settingsRes, partnerSettingsRes, partnerListingsRes]) => {
        const init = initRes.data || {};
        const settings = settingsRes.data || null;
        const partnerSettings = partnerSettingsRes.data || {};
        
        const prefill = location.state?.createCampaign || null;
        const rawSelected = asArray(prefill?.hoarding_ids);
        const allPrimaryHoardings = Array.isArray(init.hoardings) ? init.hoardings : [];
        const hoardingIdByPublicId = new Map(allPrimaryHoardings.map((h) => [h.hoarding_id, h.id]));
        const selected = [...new Set(rawSelected.map((id) => (
          allPrimaryHoardings.some((h) => h.id === id) ? id : hoardingIdByPublicId.get(id) || id
        )))];
        const selectedSet = new Set(selected);
        const nextHoardings = allPrimaryHoardings
          .filter((h) => h.occupancy_status !== "Blocked" || selectedSet.has(h.id));
        const nextPartnerHoardings = asArray(partnerListingsRes.data)
          .filter((listing) => listing.status === "approved" && listing.partner_visibility_status === "available")
          .map(partnerListingToCampaignHoarding);
        const nextMargins = Array.isArray(init.margins) ? init.margins : [];
        const nextClients = Array.isArray(init.clients) ? init.clients : [];
        
        setHoardings(nextHoardings);
        setPartnerHoardings(nextPartnerHoardings);
        setShowPartnerInventory(!!partnerSettings.enabled && !!partnerSettings.use_partner_inventory_enabled);
        setBusinessMargins(nextMargins);
        setClients(nextClients);
        setAccountingSettings(settings);

        const effectiveMargins = latestBusinessMargins(nextMargins, prefill?.start_date || EMPTY.start_date);
        const matchedClient = prefill?.client_id
          ? nextClients.find((client) => client.id === prefill.client_id)
          : nextClients.find((client) => String(client.company || "").trim().toLowerCase() === String(prefill?.client_name || "").trim().toLowerCase());
        
        const contractAmounts = {};
        selected.forEach((hId, index) => {
          const h = [...nextHoardings, ...nextPartnerHoardings].find((item) => item.id === hId);
          if (h) {
            const rawId = rawSelected[index];
            contractAmounts[hId] = defaultChargeForHoarding(h, effectiveMargins, prefill?.contract_amounts?.[hId] || prefill?.contract_amounts?.[rawId]);
          }
        });
        Object.entries(prefill?.contract_amounts || {}).forEach(([key, value]) => {
          if (String(key).startsWith("partner:")) {
            contractAmounts[key] = value;
          }
        });

        const gstDefaults = settings ? {
          gst_mode: settings.default_gst_mode || "exclusive",
          gst_percentage: settings.default_gst_percentage || 18,
          cgst_percentage: settings.default_cgst_percentage || 9,
          sgst_percentage: settings.default_sgst_percentage || 9,
          igst_percentage: settings.default_igst_percentage || 18,
          gst_treatment: "cgst_sgst",
          round_off_behavior: settings.default_round_off || "none",
        } : EMPTY.gst;

        const partnerUsageIds = asArray(prefill?.partner_usage_ids);
        setForm({
          ...EMPTY,
          ...(prefill || {}),
          client_id: matchedClient?.id || prefill?.client_id || null,
          client_name: matchedClient?.company || prefill?.client_name || "",
          client_mobile: matchedClient?.mobile || prefill?.client_mobile || "",
          city: "",
          hoarding_ids: selected,
          contract_amounts: contractAmounts,
          partner_usage_ids: partnerUsageIds,
          gst: prefill?.gst || gstDefaults,
          start_date: prefill?.start_date ? prefill.start_date.slice(0, 10) : today,
          end_date: prefill?.end_date ? prefill.end_date.slice(0, 10) : "",
        });
        setInitialSelectedIds(selected);
      })
      .catch((err) => {
        toast.error(formatApiError(err.response?.data?.detail) || "Failed to load initialization settings");
      })
      .finally(() => setLoading(false));
  }, [location.state, today]);

  useEffect(() => {
    if (!showPartnerInventory && inventoryTab === "partner") {
      setInventoryTab("primary");
    }
  }, [showPartnerInventory, inventoryTab]);

  const activeHoardings = inventoryTab === "partner" ? partnerHoardings : hoardings;
  const pickableHoardings = useMemo(() => {
    const term = committedHoardingSearch.trim().toLowerCase();
    const filtered = activeHoardings
      .filter((h) => !hoardingFilters.city || h.city === hoardingFilters.city)
      .filter((h) => !hoardingFilters.type || h.type === hoardingFilters.type)
      .filter((h) => {
        if (!term) return true;
        return h.name?.toLowerCase().includes(term) || h.hoarding_id?.toLowerCase().includes(term);
      });

    // Sort initially selected hoardings to the top
    return [...filtered].sort((a, b) => {
      const aSel = initialSelectedIds.includes(a.id);
      const bSel = initialSelectedIds.includes(b.id);
      if (aSel && !bSel) return -1;
      if (!aSel && bSel) return 1;
      return 0;
    });
  }, [activeHoardings, hoardingFilters.city, hoardingFilters.type, committedHoardingSearch, initialSelectedIds]);

  const totalDisplayPages = Math.max(1, Math.ceil(pickableHoardings.length / hoardingPageSize));
  const pageItems = useMemo(() => {
    return pickableHoardings.slice((hoardingPage - 1) * hoardingPageSize, hoardingPage * hoardingPageSize);
  }, [pickableHoardings, hoardingPage, hoardingPageSize]);
  const selectedHoardings = useMemo(() => {
    const selectedSet = new Set(form.hoarding_ids);
    return allAssignableHoardings.filter((h) => selectedSet.has(h.id));
  }, [form.hoarding_ids, allAssignableHoardings]);

  function pickClient(id) {
    const cl = clients.find((c) => c.id === id);
    setForm((p) => ({
      ...p,
      client_id: id,
      client_name: cl?.company || "",
      client_mobile: cl?.mobile || "",
    }));
  }

  function toggleHoarding(hId, checked) {
    const hoarding = allAssignableHoardings.find((item) => item.id === hId);
    if (checked && hoarding?.occupancy_status === "Blocked" && !initialSelectedIds.includes(hId)) {
      toast.error("Blocked hoardings cannot be added to campaigns");
      return;
    }
    setForm((p) => {
      const ids = checked ? [...p.hoarding_ids, hId] : p.hoarding_ids.filter((x) => x !== hId);
      const amounts = { ...p.contract_amounts };
      if (checked) {
        const h = allAssignableHoardings.find((item) => item.id === hId);
        const effectiveMargins = latestBusinessMargins(businessMargins, p.start_date || today);
        amounts[hId] = defaultChargeForHoarding(h, effectiveMargins, amounts[hId]);
      } else {
        delete amounts[hId];
      }
      return { ...p, hoarding_ids: ids, contract_amounts: amounts };
    });
    if (!checked) {
      setExpandedChargeRows((prev) => {
        const next = new Set(prev);
        next.delete(hId);
        return next;
      });
    }
  }

  function toggleChargeRow(hId) {
    setExpandedChargeRows((prev) => {
      const next = new Set(prev);
      if (next.has(hId)) next.delete(hId); else next.add(hId);
      return next;
    });
  }

  function updateHoardingCharge(hId, field, value) {
    setForm((p) => ({
      ...p,
      contract_amounts: updateCharge(p.contract_amounts, hId, field, value),
    }));
  }

  function updateGst(field, value) {
    setForm((p) => {
      const gst = { ...(p.gst || {}) };
      if (field === "gst_percentage") {
        const pct = Number(value || 0);
        return {
          ...p,
          gst: {
            ...gst,
            gst_percentage: value,
            ...(gst.gst_treatment === "igst"
              ? { igst_percentage: value }
              : { cgst_percentage: pct / 2, sgst_percentage: pct / 2 }),
          },
        };
      }
      if (field === "gst_treatment") {
        const pct = Number(gst.gst_percentage || accountingSettings?.default_gst_percentage || 18);
        return {
          ...p,
          gst: {
            ...gst,
            gst_treatment: value,
            ...(value === "igst"
              ? { igst_percentage: gst.igst_percentage || pct }
              : { cgst_percentage: gst.cgst_percentage || pct / 2, sgst_percentage: gst.sgst_percentage || pct / 2 }),
          },
        };
      }
      if (field === "igst_percentage") return { ...p, gst: { ...gst, igst_percentage: value, gst_percentage: value } };
      if (field === "cgst_percentage" || field === "sgst_percentage") {
        const next = { ...gst, [field]: value };
        next.gst_percentage = Number(next.cgst_percentage || 0) + Number(next.sgst_percentage || 0);
        return { ...p, gst: next };
      }
      return { ...p, gst: { ...gst, [field]: value } };
    });
  }

  function contractAmountsForSave() {
    return Object.fromEntries(Object.entries(form.contract_amounts || {}).map(([hId, value]) => {
      const hoarding = allAssignableHoardings.find((item) => item.id === hId);
      return [hId, withPartnerDisplayMarginDefaults(chargeFor(value), hoarding)];
    }));
  }

  const cities = useMemo(() => {
    return [...new Set(activeHoardings.map((h) => h.city).filter(Boolean))].sort();
  }, [activeHoardings]);
  const activeTypes = useMemo(() => {
    return [...new Set([...hoardingTypes, ...activeHoardings.map((h) => h.type)].filter(Boolean))].sort();
  }, [activeHoardings, hoardingTypes]);
  const gstTreatment = form.gst?.gst_treatment || "cgst_sgst";

  async function save() {
    if (!form.name.trim()) { toast.error("Campaign name is required"); return; }
    if (!form.client_id) { toast.error("Client is required"); return; }
    if (!form.start_date) { toast.error("Start date is required"); return; }
    if (!form.is_open_ended && !form.end_date) { toast.error("End date is required unless this is open-ended."); return; }
    if (!form.is_open_ended && new Date(form.end_date) <= new Date(form.start_date)) { toast.error("End date must be after start date"); return; }
    if (form.hoarding_ids.length === 0 && asArray(form.partner_usage_ids).length === 0) { toast.error("Select at least one hoarding"); return; }
    if (!form.proposal_id && form.hoarding_ids.some(isPartnerHoardingId)) {
      toast.error("Partner Network hoardings need owner approval from a proposal before campaign creation.");
      return;
    }
    const marginError = validateCampaignMargins(form);
    if (marginError) { toast.error(marginError); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        contract_amounts: contractAmountsForSave(),
        end_date: form.is_open_ended ? null : form.end_date,
        expected_end_date: form.expected_end_date || null,
        payment_due_date: form.payment_due_date || null,
        end_date_status: form.is_open_ended ? (form.end_date_status || "unknown") : (form.end_date_status || "confirmed"),
        po_value_status: form.po_value_status || "confirmed",
        estimated_daily_charge: Number(form.estimated_daily_charge || 0),
        estimated_monthly_charge: Number(form.estimated_monthly_charge || 0),
        estimated_po_value: Number(form.estimated_po_value || 0),
        confirmed_po_value: Number(contractTotal || 0),
        gst: {
          ...(form.gst || {}),
          gst_percentage: Number(form.gst?.gst_percentage || 0),
          cgst_percentage: Number(form.gst?.cgst_percentage || 0),
          sgst_percentage: Number(form.gst?.sgst_percentage || 0),
          igst_percentage: Number(form.gst?.igst_percentage || 0),
        },
      };
      delete payload.city;

      const { data } = await api.post("/campaigns", payload);
      toast.success("Campaign created successfully");
      if (form.proposal_id) {
        delete globalThis.__hoardexaSentProposalsCache;
      }
      nav(`/campaigns/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    nav("/campaigns");
  }

  const actions = (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={handleCancel}>Cancel</Button>
      <Button onClick={save} disabled={saving}><Save size={15} /> {saving ? "Creating..." : "Create"}</Button>
    </div>
  );

  return (
    <Layout title="New campaign" subtitle="Create campaign workspace and assign hoardings" actions={actions}>
      {loading ? (
        <HoardexaLoader label="Loading campaign workspace" />
      ) : (
        <div className="hx-hoardings-page max-w-6xl">
          {form.proposal_id && (
            <Section
              title="Proposal handoff"
              right={<span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">{form.hoarding_ids.length + asArray(form.partner_usage_ids).length} hoardings selected</span>}
            >
              <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                <div>
                  <div className="text-[11px] font-black uppercase text-slate-500">Client</div>
                  <div className="mt-1 font-bold text-slate-950">{form.client_name || "Select client"}</div>
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase text-slate-500">Campaign</div>
                  <div className="mt-1 font-bold text-slate-950">{form.name || "Campaign name"}</div>
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase text-slate-500">PO value</div>
                  <div className="mt-1 font-bold text-slate-950">{inr(contractTotal)}</div>
                </div>
              </div>
              {selectedHoardings.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {selectedHoardings.slice(0, 8).map((hoarding) => (
                    <span key={hoarding.id} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-700">
                      {hoarding.hoarding_id || hoarding.name}
                    </span>
                  ))}
                  {selectedHoardings.length > 8 && (
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-500">+{selectedHoardings.length - 8} more</span>
                  )}
                </div>
              )}
              {!form.client_id && (
                <p className="mt-3 text-xs font-semibold text-amber-700">Select the matching client before creating this campaign.</p>
              )}
            </Section>
          )}

          <Section title="Campaign basics">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Campaign name" required>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Summer 2026 Launch" />
              </Field>
              <Field label="Client" required>
                <Select value={form.client_id || "none"} onValueChange={(v) => v === "none" ? setForm({ ...form, client_id: null, client_name: "" }) : pickClient(v)}>
                  <SelectTrigger><SelectValue placeholder="Pick a client" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {clients.map((cl) => <SelectItem key={cl.id} value={cl.id}>{cl.company}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Payment due date">
                <Input type="date" value={form.payment_due_date || ""} onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })} />
              </Field>
            </div>
          </Section>

          <Section title="Dates">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Start date" required>
                <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </Field>
              <Field label="End date" required={!form.is_open_ended}>
                <Input type="date" value={form.end_date || ""} disabled={form.is_open_ended} onChange={(e) => setForm({ ...form, end_date: e.target.value, end_date_status: "confirmed" })} />
              </Field>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 sm:col-span-2">
                <Checkbox checked={!!form.is_open_ended} onCheckedChange={(checked) => setForm({ ...form, is_open_ended: !!checked, end_date: checked ? "" : form.end_date, end_date_status: checked ? "unknown" : "confirmed" })} />
                End date not known yet
              </label>
              {form.is_open_ended && (
                <>
                  <Field label="Expected / Tentative End Date">
                    <Input type="date" value={form.expected_end_date || ""} onChange={(e) => setForm({ ...form, expected_end_date: e.target.value, end_date_status: e.target.value ? "tentative" : "unknown" })} />
                  </Field>
                  <Field label="End Date Status">
                    <Select value={form.end_date_status || "unknown"} onValueChange={(v) => setForm({ ...form, end_date_status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unknown">Unknown</SelectItem>
                        <SelectItem value="tentative">Tentative</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </>
              )}
            </div>
          </Section>

          {canViewRevenue && (
            <Section
              title="PO and breakup control"
              right={<span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700">Breakup {inr(contractTotal)}</span>}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="PO status">
                  <Select value={form.po_value_status || "confirmed"} onValueChange={(v) => setForm({ ...form, po_value_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="estimated">Estimated</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Confirmed PO value">
                  <div className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-900">
                    {inr(contractTotal)}
                  </div>
                </Field>
              </div>

              {form.po_value_status !== "confirmed" && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Estimated PO Value">
                    <Input type="number" min="0" value={form.estimated_po_value || ""} onChange={(e) => setForm({ ...form, estimated_po_value: e.target.value })} />
                  </Field>
                  <Field label="Estimated Monthly Charge">
                    <Input type="number" min="0" value={form.estimated_monthly_charge || ""} onChange={(e) => setForm({ ...form, estimated_monthly_charge: e.target.value })} />
                  </Field>
                  <Field label="Estimated Daily Charge">
                    <Input type="number" min="0" value={form.estimated_daily_charge || ""} onChange={(e) => setForm({ ...form, estimated_daily_charge: e.target.value })} />
                  </Field>
                </div>
              )}

            </Section>
          )}

          {canViewRevenue && (
            <Section title="GST & Taxation Settings">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <Field label="GST Mode">
                  <Select value={form.gst?.gst_mode || "exclusive"} onValueChange={(v) => updateGst("gst_mode", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exclusive">Exclusive</SelectItem>
                      <SelectItem value="inclusive">Inclusive</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="GST %">
                  <Input type="number" min="0" value={form.gst?.gst_percentage ?? accountingSettings?.default_gst_percentage ?? 18} onChange={(e) => updateGst("gst_percentage", e.target.value)} />
                </Field>
                <Field label="Split/Treatment">
                  <Select value={gstTreatment} onValueChange={(v) => updateGst("gst_treatment", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cgst_sgst">CGST + SGST</SelectItem>
                      <SelectItem value="igst">IGST</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {gstTreatment === "igst" ? (
                  <Field label="IGST %">
                    <Input type="number" min="0" value={form.gst?.igst_percentage ?? accountingSettings?.default_igst_percentage ?? 18} onChange={(e) => updateGst("igst_percentage", e.target.value)} />
                  </Field>
                ) : (
                  <>
                    <Field label="CGST %">
                      <Input type="number" min="0" value={form.gst?.cgst_percentage ?? accountingSettings?.default_cgst_percentage ?? 9} onChange={(e) => updateGst("cgst_percentage", e.target.value)} />
                    </Field>
                    <Field label="SGST %">
                      <Input type="number" min="0" value={form.gst?.sgst_percentage ?? accountingSettings?.default_sgst_percentage ?? 9} onChange={(e) => updateGst("sgst_percentage", e.target.value)} />
                    </Field>
                  </>
                )}
                <Field label="Round Off">
                  <Select value={form.gst?.round_off_behavior || "none"} onValueChange={(v) => updateGst("round_off_behavior", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="nearest_rupee">Nearest rupee</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </Section>
          )}

          <Section
            title="Assign Hoardings"
            right={<span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">{form.hoarding_ids.length} selected</span>}
          >
            <div className="space-y-3">
              {showPartnerInventory && (
                <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setInventoryTab("primary");
                      setHoardingFilters({ city: "", type: "" });
                      setHoardingPage(1);
                    }}
                    className={`rounded-md px-3 py-1.5 text-xs font-black transition ${
                      inventoryTab === "primary" ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    My Hoardings
                    {selectedPrimaryCount > 0 && <span className="ml-1 text-blue-600">{selectedPrimaryCount}</span>}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setInventoryTab("partner");
                      setHoardingFilters({ city: "", type: "" });
                      setHoardingPage(1);
                    }}
                    className={`rounded-md px-3 py-1.5 text-xs font-black transition ${
                      inventoryTab === "partner" ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    Partner n/w
                    {selectedPartnerCount > 0 && <span className="ml-1 text-blue-600">{selectedPartnerCount}</span>}
                  </button>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-3">
                <label className="flex flex-col gap-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  <span>City</span>
                  <Select value={hoardingFilters.city || "all"} onValueChange={(v) => { setHoardingFilters({ ...hoardingFilters, city: v === "all" ? "" : v }); setHoardingPage(1); }}>
                    <SelectTrigger className="w-[150px] h-[34px] text-xs bg-white"><SelectValue placeholder="All Cities" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </label>

                <label className="flex flex-col gap-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  <span>Type</span>
                  <Select value={hoardingFilters.type || "all"} onValueChange={(v) => { setHoardingFilters({ ...hoardingFilters, type: v === "all" ? "" : v }); setHoardingPage(1); }}>
                    <SelectTrigger className="w-[150px] h-[34px] text-xs bg-white"><SelectValue placeholder="All Types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {activeTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      {activeTypes.length === 0 && <SelectItem value="__no_types" disabled>No types yet</SelectItem>}
                    </SelectContent>
                  </Select>
                </label>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <RegisterSearch
                    value={hoardingSearch}
                    onChange={setHoardingSearch}
                    onSearch={() => {
                      setCommittedHoardingSearch(hoardingSearch.trim());
                      setHoardingPage(1);
                    }}
                    placeholder="Search Hoardings..."
                    testId="campaign-hoardings-search"
                    ariaLabel="Search hoardings"
                  />
                  <Select value={String(hoardingPageSize)} onValueChange={(v) => { setHoardingPageSize(Number(v)); setHoardingPage(1); }}>
                    <SelectTrigger className="w-[120px] h-[34px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 per page</SelectItem>
                      <SelectItem value="25">25 per page</SelectItem>
                      <SelectItem value="50">50 per page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                {pageItems.map((hoarding) => {
                  const checked = form.hoarding_ids.includes(hoarding.id);
                  const isPartnerInventory = hoarding.inventory_source === "partner";
                  const charge = withPartnerDisplayMarginDefaults(chargeFor(form.contract_amounts?.[hoarding.id]), hoarding);
                  const displayProfit = marginProfit(charge.display, charge.display_margin_type, charge.display_margin_value);
                  const printingProfit = marginProfit(charge.printing, charge.printing_margin_type, charge.printing_margin_value);
                  const installationProfit = marginProfit(charge.installation, charge.installation_margin_type, charge.installation_margin_value);
                  const chargeOpen = expandedChargeRows.has(hoarding.id);
                  const previewCharge = checked
                    ? charge
                    : defaultChargeForHoarding(hoarding, latestBusinessMargins(businessMargins, form.start_date || today));
                  const previewDisplayProfit = marginProfit(previewCharge.display, previewCharge.display_margin_type, previewCharge.display_margin_value);
                  const rowTotal = campaignChargeTotal(previewCharge, form.start_date, form.end_date, form.is_open_ended);
                  return (
                    <div
                      key={hoarding.id}
                      className={`rounded-[10px] border px-3 py-1.5 transition ${checked ? "border-blue-200 bg-blue-50/70" : "border-[#E2E8F0] bg-white hover:border-[#93C5FD] hover:bg-[#F8FAFC]"}`}
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <Checkbox checked={checked} disabled={!canAssignHoardings} onCheckedChange={(value) => toggleHoarding(hoarding.id, value)} className="shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 flex-col gap-1.5 md:flex-row md:items-center md:justify-between">
                            <div className="min-w-0">
                              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                                <span className="truncate text-[12px] font-extrabold text-[#0F172A]">{hoarding.hoarding_id || "No code"}</span>
                                <h3 className="min-w-0 truncate font-bold text-[#0F172A]" title={hoarding.name}>{hoarding.name || "Untitled hoarding"}</h3>
                                {isPartnerInventory && <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-bold text-emerald-700">Profit {inr(previewDisplayProfit)}</span>}
                                {isPartnerInventory && hoarding.owner_name && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-[#0F172A]">{hoarding.owner_name}</span>}
                                {hoarding.type && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-[#0F172A]">{hoarding.type}</span>}
                                {hoarding.city && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-[#0F172A]">{hoarding.city}</span>}
                                {hoarding.width && hoarding.height && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-[#0F172A]">{hoarding.width} x {hoarding.height}</span>}
                              </div>
                            </div>
                            <button
                              type="button"
                              disabled={!checked || !canViewRevenue}
                              onClick={() => toggleChargeRow(hoarding.id)}
                              className={`shrink-0 rounded-lg px-2 py-1 text-left transition md:text-right ${checked && canViewRevenue ? "cursor-pointer hover:bg-white/80 hover:ring-1 hover:ring-blue-200" : "cursor-default"}`}
                              title={checked && canViewRevenue ? (chargeOpen ? "Hide breakup" : "Edit breakup") : undefined}
                            >
                              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Campaign total</div>
                              <div className="text-[13px] font-extrabold text-[#0F172A]">{inr(rowTotal)}</div>
                            </button>
                          </div>
                        </div>
                      </div>
                      {checked && canViewRevenue && chargeOpen && (
                        <div className="mt-2 grid grid-cols-1 gap-2 border-t border-slate-200/60 pt-2 md:grid-cols-3">
                          {isPartnerInventory ? (
                            <div className="space-y-1">
                              <Field label={`Display profit ${inr(displayProfit)}`}>
                                <Input type="number" min="0" value={charge.display || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "display", e.target.value)} className="h-8 text-xs bg-white" />
                              </Field>
                              <div className="grid grid-cols-[1fr_80px] gap-1">
                                <Select value={charge.display_margin_type || "percent"} onValueChange={(v) => updateHoardingCharge(hoarding.id, "display_margin_type", v)} disabled={!canEditAmounts}>
                                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="percent">%</SelectItem>
                                    <SelectItem value="amount">₹</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Input type="number" min="0" value={charge.display_margin_value || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "display_margin_value", e.target.value)} className="h-8 text-xs bg-white" />
                              </div>
                            </div>
                          ) : (
                            <Field label="Display"><Input type="number" min="0" value={charge.display || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "display", e.target.value)} className="h-8 text-xs bg-white" /></Field>
                          )}
                          <Field label={isPartnerInventory ? `Printing profit ${inr(printingProfit)}` : "Printing"}><Input type="number" min="0" value={charge.printing || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "printing", e.target.value)} className="h-8 text-xs bg-white" /></Field>
                          <Field label={isPartnerInventory ? `Mounting profit ${inr(installationProfit)}` : "Mounting"}><Input type="number" min="0" value={charge.installation || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "installation", e.target.value)} className="h-8 text-xs bg-white" /></Field>
                        </div>
                      )}
                    </div>
                  );
                })}
                {pageItems.length === 0 && (
                  <div className="py-8 text-center text-xs font-semibold text-slate-500">No hoardings found.</div>
                )}
              </div>

              {totalDisplayPages > 1 && (
                <div className="border-t border-slate-100 pt-3">
                  <PaginationFooter page={hoardingPage} pageSize={hoardingPageSize} total={pickableHoardings.length} onPageChange={setHoardingPage} />
                </div>
              )}
            </div>
          </Section>

          <Section title="Notes">
            <Textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional campaign remarks..." />
          </Section>
        </div>
      )}
    </Layout>
  );
}
